import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/format";

export const money = (v, digits = 0) => (v == null || isNaN(Number(v)) ? "-" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits })}`);
export const compact = (v) => formatCurrency(v);

function Field({ label, suffix, value, onChange, step = "any", min = 0, testid, hint }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" step={step} min={min} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="h-9 pr-9 text-sm" data-testid={testid} />
        {suffix && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function AssumptionsPanel({ assumptions, onChange, postMoney }) {
  const a = assumptions;
  const set = (k, v) => onChange({ ...a, [k]: v });
  const us = a.tax_mode !== "simple";
  return (
    <div className="space-y-5" data-testid="assumptions-panel">
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Further dilution before exit</Label>
          <span className="text-xs font-semibold tabular-nums text-foreground" data-testid="assumption-dilution-value">{Math.round(Number(a.expected_dilution_pct ?? 20))}%</span>
        </div>
        <Slider value={[Number(a.expected_dilution_pct ?? 20)]} min={0} max={80} step={1} onValueChange={([v]) => set("expected_dilution_pct", v)} className="mt-2" data-testid="assumption-dilution" />
        <p className="mt-1 text-[11px] text-muted-foreground">Future rounds and pool top-ups shrink every existing share. 15–25% per round is typical.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Years to exit" value={a.exit_years} onChange={(v) => set("exit_years", v)} step="0.5" testid="assumption-exit-years" />
        <Field label="Volatility" suffix="%" value={a.volatility_pct} onChange={(v) => set("volatility_pct", v)} testid="assumption-volatility" hint="Private early-stage: 50–80%" />
        <Field label="Risk-free rate" suffix="%" value={a.risk_free_pct} onChange={(v) => set("risk_free_pct", v)} testid="assumption-risk-free" />
        <Field label="Expected term" suffix="yrs" value={a.expected_term_years} onChange={(v) => set("expected_term_years", v)} testid="assumption-term" hint="Black-Scholes only" />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Tax view</Label>
          <div className="inline-flex rounded-full border bg-card p-0.5 text-xs" role="tablist">
            <button type="button" onClick={() => set("tax_mode", "us")} className={`rounded-full px-3 py-1 font-semibold ${us ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tax-mode-us">US ISO/NSO</button>
            <button type="button" onClick={() => set("tax_mode", "simple")} className={`rounded-full px-3 py-1 font-semibold ${!us ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tax-mode-simple">Simple rate</button>
          </div>
        </div>
        {us ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Ordinary income" suffix="%" value={a.ordinary_rate_pct} onChange={(v) => set("ordinary_rate_pct", v)} testid="tax-ordinary" />
            <Field label="Long-term cap gains" suffix="%" value={a.ltcg_rate_pct} onChange={(v) => set("ltcg_rate_pct", v)} testid="tax-ltcg" />
            <Field label="AMT" suffix="%" value={a.amt_rate_pct} onChange={(v) => set("amt_rate_pct", v)} testid="tax-amt" />
            <Field label="State" suffix="%" value={a.state_rate_pct} onChange={(v) => set("state_rate_pct", v)} testid="tax-state" />
          </div>
        ) : (
          <div className="mt-3"><Field label="Your combined tax rate" suffix="%" value={a.simple_rate_pct} onChange={(v) => set("simple_rate_pct", v)} testid="tax-simple-rate" hint="Applied to the whole gain, pre-tax → after-tax." /></div>
        )}
      </div>
      <div>
        <Label className="text-xs">Exit values to model</Label>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(a.exit_values || []).map((v, i) => (
            <Input key={i} type="number" step="any" min={0} value={v ?? ""} onChange={(e) => { const next = [...a.exit_values]; next[i] = e.target.value === "" ? "" : Number(e.target.value); set("exit_values", next); }} className="h-9 text-sm" data-testid={`exit-value-${i}`} />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(a.exit_values || []).length < 8 && (
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => set("exit_values", [...(a.exit_values || []), Math.round((postMoney || 10_000_000) * 3)])} data-testid="exit-value-add">+ Add scenario</Button>
          )}
          {(a.exit_values || []).length > 1 && (
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => set("exit_values", a.exit_values.slice(0, -1))} data-testid="exit-value-remove">Remove last</Button>
          )}
          {postMoney ? <span className="self-center text-[11px] text-muted-foreground">Current implied valuation {compact(postMoney)}</span> : null}
        </div>
      </div>
    </div>
  );
}
